import * as React from "react"

import Button from "../components/Button"
import ExternalLink from "../components/ExternalLink"
import { ArrowRightIcon, ArrowUpRightIcon } from "@heroicons/react/24/outline"

const profiles = [
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/in/cary-stothart-phd-0102402a/",
  },
  { label: "GitHub", href: "https://github.com/cstothart" },
  {
    label: "ResearchGate",
    href: "https://www.researchgate.net/profile/Cary-Stothart",
  },
  {
    label: "Google Scholar",
    href: "https://scholar.google.com/citations?user=ebpys10AAAAJ&hl=en",
  },
]

const ContactPage = () => {
  const [name, setName] = React.useState({
    value: "",
    hasBeenBlurred: false,
    isError: false,
  })
  const [email, setEmail] = React.useState({
    value: "",
    hasBeenBlurred: false,
    isError: false,
  })
  const [message, setMessage] = React.useState({
    value: "",
    hasBeenBlurred: false,
    isError: false,
  })
  const [isFormComplete, setIsFormComplete] = React.useState(false)
  const [isFormSubmitted, setIsFormSubmitted] = React.useState(false)
  const [isSubmissionError, setIsSubmissionError] = React.useState(false)
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  const getEmailValidity = (email: string) => {
    let emailIsValid = true
    const emailRegex = /^(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/
    if (!emailRegex.test(email.trim().toLowerCase())) {
      emailIsValid = false
    }
    return emailIsValid
  }

  const areAllFieldsValid = () => {
    let areFieldsValid = true

    if (name.value.trim().length === 0) {
      areFieldsValid = false
      if (name.hasBeenBlurred) {
        setName(name => ({ ...name, isError: true }))
      }
    } else {
      setName(name => ({ ...name, isError: false }))
    }

    if (
      email.value.trim().length === 0 ||
      !getEmailValidity(email.value.trim())
    ) {
      areFieldsValid = false
      if (email.hasBeenBlurred) {
        setEmail(email => ({ ...email, isError: true }))
      }
    } else {
      setEmail(email => ({ ...email, isError: false }))
    }

    if (message.value.trim().length === 0) {
      areFieldsValid = false
      if (message.hasBeenBlurred) {
        setMessage(message => ({ ...message, isError: true }))
      }
    } else {
      setMessage(message => ({ ...message, isError: false }))
    }

    return areFieldsValid
  }

  const handleInputChange = event => {
    switch (event.target.name) {
      case "name":
        setName(name => ({ ...name, value: event.target.value }))
        break
      case "email":
        setEmail(email => ({ ...email, value: event.target.value }))
        break
      case "message":
        setMessage(message => ({ ...message, value: event.target.value }))
        break
    }
  }

  const handleBlur = event => {
    switch (event.target.name) {
      case "name":
        setName(name => ({ ...name, hasBeenBlurred: true }))
        break
      case "email":
        setEmail(email => ({ ...email, hasBeenBlurred: true }))
        break
      case "message":
        setMessage(message => ({ ...message, hasBeenBlurred: true }))
        break
    }
  }

  React.useEffect(() => {
    if (areAllFieldsValid()) {
      setIsFormComplete(true)
    } else {
      setIsFormComplete(false)
    }
  }, [
    name.value,
    name.hasBeenBlurred,
    email.value,
    email.hasBeenBlurred,
    message.value,
    message.hasBeenBlurred,
  ])

  const encode = data => {
    return Object.keys(data)
      .map(key => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
      .join("&")
  }

  const handleSubmit = event => {
    event.preventDefault()
    setIsSubmitting(true)
    setIsSubmissionError(false)
    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: encode({
        "form-name": "contact",
        name: name.value,
        email: email.value,
        message: message.value,
      }),
    })
      .then(res => {
        if (!res.ok) throw new Error("Contact form submission failed")
        setIsFormSubmitted(true)
      })
      .catch(() => {
        setIsSubmissionError(true)
      })
      .finally(() => setIsSubmitting(false))
  }

  const inputWrapperStyle = (isError: boolean) =>
    `input-wrapper w-full${isError ? " input-wrapper--error" : ""}`

  const inputStyle = "input w-full"

  const errorStyle = "form-error text-xs font-semibold pl-3 mt-1"

  return (
    <>
      <div className="contact-layout">
        {isFormSubmitted ? (
          <div className="contact-success" role="status">
            <h1 className="page-heading">Message sent</h1>
            <p>
              Thanks for reaching out! I'll try to respond to your message as
              soon as possible.
            </p>
          </div>
        ) : (
          <>
            <div className="contact-intro">
              <h1 className="page-heading">Contact me</h1>
              <p className="contact-intro__lead">
                Reach out if you'd like to talk about research, technology, or a
                possible collaboration.
              </p>
              <div className="contact-profiles">
                {profiles.map(profile => (
                  <ExternalLink key={profile.label} href={profile.href}>
                    <span>{profile.label}</span>
                    <ArrowUpRightIcon aria-hidden="true" />
                  </ExternalLink>
                ))}
              </div>
            </div>
            <form
              name="contact"
              method="POST"
              data-netlify="true"
              data-netlify-honeypot="bot-field"
              className="contact-form"
              onSubmit={handleSubmit}
            >
              <input type="hidden" name="form-name" value="contact" />
              <p hidden>
                <label>
                  Don't fill this out:
                  <input name="bot-field" onChange={handleInputChange} />
                </label>
              </p>
              <div className="contact-form__field">
                <label className="contact-form__label" htmlFor="contact-name">
                  Name
                </label>
                <div className={inputWrapperStyle(name.isError)}>
                  <input
                    id="contact-name"
                    className={`${inputStyle}`}
                    type="text"
                    name="name"
                    autoComplete="name"
                    required
                    placeholder="Jane Doe"
                    value={name.value}
                    aria-invalid={name.isError}
                    aria-describedby={
                      name.isError ? "contact-name-error" : undefined
                    }
                    onBlur={handleBlur}
                    onChange={handleInputChange}
                  />
                </div>
                {name.isError && (
                  <p id="contact-name-error" className={errorStyle}>
                    What is your name?
                  </p>
                )}
              </div>
              <div className="contact-form__field">
                <label className="contact-form__label" htmlFor="contact-email">
                  Email
                </label>
                <div className={inputWrapperStyle(email.isError)}>
                  <input
                    id="contact-email"
                    className={`${inputStyle}`}
                    type="email"
                    name="email"
                    autoComplete="email"
                    required
                    placeholder="jane@university.edu"
                    value={email.value}
                    aria-invalid={email.isError}
                    aria-describedby={
                      email.isError ? "contact-email-error" : undefined
                    }
                    onBlur={handleBlur}
                    onChange={handleInputChange}
                  />
                </div>
                {email.isError && (
                  <p id="contact-email-error" className={errorStyle}>
                    Please enter a valid email.
                  </p>
                )}
              </div>
              <div className="contact-form__field">
                <label
                  className="contact-form__label"
                  htmlFor="contact-message"
                >
                  Message
                </label>
                <div className={inputWrapperStyle(message.isError)}>
                  <textarea
                    id="contact-message"
                    className={`${inputStyle} contact-form__message`}
                    name="message"
                    required
                    placeholder="What would you like to talk about?"
                    value={message.value}
                    aria-invalid={message.isError}
                    aria-describedby={
                      message.isError ? "contact-message-error" : undefined
                    }
                    onBlur={handleBlur}
                    onChange={handleInputChange}
                  ></textarea>
                </div>
                {message.isError && (
                  <p id="contact-message-error" className={errorStyle}>
                    What is your message?
                  </p>
                )}
              </div>

              <div className="contact-form__actions">
                <div className="contact-form__submit">
                  <Button
                    disabled={!isFormComplete || isSubmitting}
                    type="submit"
                  >
                    {isSubmitting ? "Sending..." : "Send message"}
                    {!isSubmitting && (
                      <ArrowRightIcon aria-hidden="true" className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                {isSubmissionError && (
                  <div role="alert">
                    <p className="form-error">
                      Something went wrong when contacting the server.
                    </p>
                  </div>
                )}
              </div>
            </form>
          </>
        )}
      </div>
    </>
  )
}

export default ContactPage
